import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout/Layout';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import PatientsPage from './pages/PatientsPage';
import AppointmentsPage from './pages/AppointmentsPage';
import MedicalHistoryPage from './pages/MedicalHistoryPage';
import BillingPage from './pages/BillingPage';
import PharmacyPage from './pages/PharmacyPage';
import ReportsPage from './pages/ReportsPage';
import HomePage from './pages/HomePage';
import './App.css';

// Create a professional medical teal and white theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#2C7A9C', // Classic Teal
      dark: '#1E5A73',
      light: '#33a3a3',
    },
    secondary: {
      main: '#4A9BBD', // Light Sea Green
      dark: '#008b8b',
      light: '#5f9ea0',
    },
    background: {
      default: '#f8fffe',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a3a3a',
      secondary: '#4a6a6a',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      color: '#1E5A73',
    },
    h2: {
      fontWeight: 700,
      color: '#1E5A73',
    },
    h3: {
      fontWeight: 600,
      color: '#2C7A9C',
    },
    h4: {
      fontWeight: 600,
      color: '#2C7A9C',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: 'linear-gradient(180deg, #f8fffe 0%, #f0f9f9 100%)',
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 600,
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #2C7A9C 0%, #4A9BBD 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1E5A73 0%, #2C7A9C 100%)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 24px rgba(0, 128, 128, 0.1)',
          border: '1px solid rgba(0, 128, 128, 0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/dashboard" element={
              <ProtectedRoute requiredRoles={['Admin', 'Reception', 'Pharmacy']}>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/patients" element={
              <ProtectedRoute requiredRoles={['Admin', 'Reception', 'Pharmacy']}>
                <Layout>
                  <PatientsPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/appointments" element={
              <ProtectedRoute requiredRoles={['Admin', 'Reception']}>
                <Layout>
                  <AppointmentsPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/medical-history" element={
              <ProtectedRoute requiredRoles={['Admin', 'Reception', 'Pharmacy']}>
                <Layout>
                  <MedicalHistoryPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/billing" element={
              <ProtectedRoute requiredRoles={['Admin', 'Reception', 'Pharmacy']}>
                <Layout>
                  <BillingPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/pharmacy" element={
              <ProtectedRoute requiredRoles={['Admin', 'Pharmacy']}>
                <Layout>
                  <PharmacyPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/reports" element={
              <ProtectedRoute requiredRoles={['Admin', 'Reception', 'Pharmacy']}>
                <Layout>
                  <ReportsPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
